import { z } from 'zod';
/**
 * Financeiro da mesa, sem a aba Caixa (não terminada no APOLO). As contas do painel
 * geral são as do APOLO (`/api/finance/painel-geral`): atendimentos = consultas em que
 * a pessoa veio; conversão = tratamentos vendidos sobre atendimentos de consulta;
 * resultado = vendas menos cancelamentos; crescimento = vendas contra o mesmo período
 * do ano anterior. Renovação, que no APOLO é marcada à mão, aqui é o tratamento novo
 * de quem já teve um antes.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const isoDateTime = z.iso.datetime();
const cents = z.number().int();
/**
 * CNAEs de saúde mais comuns. `fatorR`: no Simples, começa no Anexo V e vai para o III com
 * Fator R de 28% ou mais (LC 123, art. 18, §5-I e §5-M); sem ele, Anexo III direto.
 */
export const CLINIC_CNAES = [
    { code: '8630-5/03', label: 'Consultas médicas (consultório)', fatorR: true },
    { code: '8630-5/02', label: 'Médica com exames complementares', fatorR: true },
    { code: '8630-5/01', label: 'Médica com procedimentos cirúrgicos', fatorR: true },
    { code: '8630-5/04', label: 'Odontologia', fatorR: true },
    { code: '8650-0/02', label: 'Nutrição', fatorR: true },
    { code: '8650-0/03', label: 'Psicologia e psicanálise', fatorR: true },
    { code: '8650-0/01', label: 'Enfermagem', fatorR: true },
    { code: '8650-0/04', label: 'Fisioterapia', fatorR: false },
    { code: '9313-1/00', label: 'Condicionamento físico (academia)', fatorR: false },
    { code: '9602-5/02', label: 'Estética e cuidados com a beleza', fatorR: false },
];
export const clinicFinanceQuerySchema = z.object({ from: isoDate, to: isoDate });
export const clinicFinanceSchema = z.object({
    from: isoDate,
    to: isoDate,
    panel: z.object({
        attended: z.number().int().min(0),
        attendedConsults: z.number().int().min(0),
        sold: z.number().int().min(0),
        salesCents: cents,
        cancelled: z.number().int().min(0),
        cancelledCents: cents,
        /** Porcentagem inteira; null sem atendimento de consulta. */
        conversion: z.number().nullable(),
        resultCents: cents,
        ticketCents: cents.nullable(),
        /** Porcentagem inteira; null sem vendas no mesmo período do ano anterior. */
        growth: z.number().nullable(),
        lastYearSalesCents: cents,
    }),
    byTreatment: z.array(z.object({ name: z.string(), count: z.number().int(), cents })),
    renewals: z.object({
        count: z.number().int().min(0),
        cents,
        /** Parte das vendas do período que veio de renovação (inteiro). */
        share: z.number().nullable(),
        rows: z.array(z.object({
            patientId: z.uuid(),
            patientName: z.string(),
            treatment: z.string(),
            day: isoDate,
            cents: cents.nullable(),
            attention: z.boolean(),
        })),
    }),
    /** Tratamentos ativos que terminam nos próximos 60 dias: a renovação a buscar. */
    ending: z.array(z.object({
        patientId: z.uuid(),
        patientName: z.string(),
        phone: z.string().nullable(),
        treatment: z.string(),
        endDay: isoDate,
        cents: cents.nullable(),
    })),
    consults: z.object({
        count: z.number().int().min(0),
        cents,
        rows: z.array(z.object({
            transactionId: z.uuid(),
            patientId: z.uuid(),
            patientName: z.string(),
            label: z.string(),
            paidAt: isoDateTime,
            cents,
            method: z.string().nullable(),
        })),
    }),
    /** Tudo o que foi pago no período (consultas, parcelas, avulsos). */
    received: z.object({ count: z.number().int().min(0), cents }),
    /** Vencido e não pago até ontem (não depende do período). */
    billing: z.object({
        count: z.number().int().min(0),
        /** Quantas pessoas devem (uma pessoa pode ter várias parcelas vencidas). */
        people: z.number().int().min(0),
        cents,
        rows: z.array(z.object({
            transactionId: z.uuid(),
            patientId: z.uuid(),
            patientName: z.string(),
            phone: z.string().nullable(),
            label: z.string(),
            dueDay: isoDate,
            days: z.number().int().min(0),
            cents,
            /** Link de pagamento sem conta (Pix da clínica e "Já paguei"), para a cobrança. */
            payUrl: z.string().nullable().optional(),
        })),
    }),
});
/**
 * Visão geral do financeiro (estudo do financeiro, etapa 4): o que entrou e saiu, o saldo,
 * o fluxo de caixa realizado e previsto, o resultado do mês e os pontos de atenção.
 * Previsto = parcelas e repasses já agendados + a média do que entrou à vista nos últimos
 * 3 meses − as contas a pagar lançadas. Tudo em centavos.
 */
export const clinicFinanceFlowMonthSchema = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    label: z.string(),
    inCents: cents,
    outCents: cents,
    forecast: z.boolean(),
    /** Saldo no fim do mês (só quando há contas cadastradas). */
    endBalanceCents: cents.nullable(),
});
export const clinicFinanceOverviewSchema = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    today: isoDate,
    hasAccounts: z.boolean(),
    balanceCents: cents.nullable(),
    monthInCents: cents,
    monthInAvgCents: cents,
    monthOutCents: cents,
    monthOutLeftCents: cents,
    overdueCents: cents,
    overduePeople: z.number().int().min(0),
    flow: z.array(clinicFinanceFlowMonthSchema),
    /** Primeiro dia, nos próximos 90, em que o saldo previsto fica negativo. */
    negativeDay: z.object({ day: isoDate, balanceCents: cents }).nullable(),
    result: z.object({
        /** Pelo caixa: o que entrou e saiu no mês. */
        cash: z.object({
            revenueCents: cents,
            feesCents: cents,
            taxesCents: cents,
            costCents: cents,
            marginCents: cents,
            fixedCents: cents,
            otherCents: cents,
            resultCents: cents,
        }),
        /** Pela competência: o que foi vendido no mês. */
        accrual: z.object({ salesCents: cents, cancelledCents: cents, netCents: cents }),
    }),
    attention: z.array(z.object({ tone: z.enum(['bad', 'wait']), tag: z.string(), text: z.string() })),
    /**
     * Meta do mês (etapa 5). Ponto de equilíbrio = despesas fixas ÷ margem de contribuição;
     * margem = o que sobra de cada real recebido depois de taxa, imposto e medicamento, nos
     * últimos 3 meses e no atual. Meta = (fixas + lucro desejado) ÷ margem.
     */
    goal: z.object({
        fixedCents: cents,
        /** Margem de contribuição em centésimos de por cento; null sem recebimentos. */
        marginBp: z.number().int().nullable(),
        breakevenCents: cents.nullable(),
        profitGoalCents: cents,
        targetCents: cents.nullable(),
        /** Vendido no mês (tratamentos que começaram, menos cancelados). */
        soldCents: cents,
        ticketCents: cents.nullable(),
        treatmentsLeft: z.number().int().min(0).nullable(),
        workdaysLeft: z.number().int().min(0),
    }),
    /** Imposto estimado do mês, para planejar: nunca substitui a guia. */
    taxes: z.object({
        regime: z.enum(['simples', 'presumido', 'indefinido']),
        cnae: z.string().nullable(),
        /** Simples: anexo do mês; null fora do Simples. */
        anexo: z.enum(['III', 'V']).nullable(),
        /** Folha de 12 meses ÷ receita de 12 meses, em centésimos de por cento. */
        fatorRBp: z.number().int().nullable(),
        /** Alíquota efetiva do mês, em centésimos de por cento. */
        rateBp: z.number().int().nullable(),
        /** Receita bruta dos 12 meses anteriores (proporcional quando a clínica é mais nova). */
        rbt12Cents: cents,
        /** Receita do mês até hoje, sobre a qual a estimativa é feita. */
        baseCents: cents,
        monthCents: cents.nullable(),
        lines: z.array(z.object({ label: z.string(), cents })),
    }),
});
/** Exportação para o contador: tudo o que entrou e saiu no mês, linha a linha. */
export const clinicFinanceExportQuerySchema = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/, 'Mês no formato AAAA-MM'),
});
export const clinicFinanceExportSchema = z.object({
    month: z.string(),
    rows: z.array(z.object({
        day: isoDate,
        kind: z.enum(['entrada', 'saida']),
        description: z.string(),
        /** Paciente ou fornecedor. */
        party: z.string(),
        category: z.string(),
        method: z.string().nullable(),
        account: z.string().nullable(),
        grossCents: cents,
        feeCents: cents,
        netCents: cents,
    })),
    summary: z.object({
        inCents: cents,
        feesCents: cents,
        outCents: cents,
        taxesEstimateCents: cents.nullable(),
        regime: z.string(),
    }),
});
// ── Pagar pelo link da cobrança (G4): página sem conta ──────────────────────
/** O que a página de pagamento mostra: só o mínimo para pagar (sem o histórico). */
export const clinicPayViewSchema = z.object({
    clinicId: z.uuid(),
    clinicName: z.string(),
    logoVersion: z.number().int().nullable(),
    patientFirstName: z.string(),
    label: z.string(),
    amountCents: z.number().int(),
    dueDay: isoDate,
    /** aberto, avisado (a pessoa disse que pagou) ou pago (a clínica deu baixa). */
    state: z.enum(['aberto', 'avisado', 'pago']),
    /** Pix copia-e-cola com o valor, quando a clínica tem a chave nos ajustes da loja. */
    pix: z.string().nullable(),
    pixName: z.string().nullable(),
    pixKeyHint: z.string().nullable(),
});
/** Linha nova no texto de cobrança com o link, quando ele existe. */
export function clinicChargeText(input) {
    const first = input.patientName.trim().split(/\s+/)[0] ?? '';
    const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(input.cents / 100);
    const tail = input.payUrl
        ? ` Para pagar pelo Pix ou avisar que já pagou: ${input.payUrl}`
        : ' Qualquer dúvida, estou à disposição.';
    return `Olá, ${first}! Tudo bem? Passando para lembrar do pagamento de ${input.label.toLowerCase()}, de ${money}, que venceu em ${input.dueDayLabel}.${tail}`;
}
